<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { getDateTimeFormatPattern } from 'shared/helpers/formatTime';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['activityLogs/getActivityLogs'].value);
const uiFlags = computed(() => getters['activityLogs/getUIFlags'].value);
const meta = computed(() => getters['activityLogs/getMeta'].value);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));

const fetchActivityLogs = page => {
  try {
    store.dispatch('activityLogs/fetch', { page });
  } catch (error) {
    const errorMessage = error?.message || t('ACTIVITY_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const getActionText = activityLog => {
  const { action, log_type, metadata, user } = activityLog;
  const userName = user?.name || t('ACTIVITY_LOGS.SYSTEM');

  // Build translation key based on log_type and action
  const translationKey = `ACTIVITY_LOGS.ACTIONS.${log_type.toUpperCase()}.${action.toUpperCase()}`;

  // Check if translation exists, otherwise use a default format
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  if (t(translationKey) !== translationKey) {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(translationKey, { user: userName, ...metadata });
  }

  // Fallback to a generic format
  return `${userName} performed ${action} on ${log_type}`;
};

const onPageChange = page => {
  router.push({ name: 'activity_logs_list', query: { page: page } });
};

onMounted(() => {
  fetchActivityLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchActivityLogs(newPage);
  }
});

const tableHeaders = computed(() => {
  return [
    t('ACTIVITY_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('ACTIVITY_LOGS.LIST.TABLE_HEADER.TIME'),
    t('ACTIVITY_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
  ];
});
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('ACTIVITY_LOGS.HEADER')"
      :description="$t('ACTIVITY_LOGS.DESCRIPTION')"
      :link-text="$t('ACTIVITY_LOGS.LEARN_MORE')"
    />

    <div class="mt-6 flex-1 text-n-slate-11">
      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('ACTIVITY_LOGS.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{ $t('ACTIVITY_LOGS.LIST.404') }}
      </p>
      <div v-else class="min-w-full overflow-x-auto">
        <table class="divide-y divide-n-weak">
          <thead>
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr v-for="activityLog in records" :key="activityLog.id">
              <td class="py-4 ltr:pr-4 rtl:pl-4 break-all whitespace-nowrap">
                {{ getActionText(activityLog) }}
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4 break-all whitespace-nowrap">
                {{
                  messageTimestamp(
                    activityLog.created_at,
                    getDateTimeFormatPattern('MMM dd, yyyy')
                  )
                }}
              </td>
              <td class="py-4 w-[8.75rem]">
                {{ activityLog.ip_address || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          class="border-n-weak border-t !px-0 py-4"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>
